<template>
  <div class="product-right-col">
    <div class="status-select">
      <label>Status</label>
      <el-select v-if="(product.status===STATUS_CODE.Active || product.status === STATUS_CODE.Disabled)" v-model="product.status_string" placeholder="Select status">
        <el-option label="Active" value="active" :selected="product.status_string === 'active'"></el-option>
        <el-option label="Disable" value="disable" :selected="product.status_string === 'disable'"></el-option>
      </el-select>
      <div v-else-if="isScheduled && !isDraft " class="status-display">Scheduled</div>
      <div v-else-if="isDraft" class="status-display">Draft</div>
    </div>

    <div class="carded">
      <div class="carded-header">
        <div class="carded-title">Recipients</div>
      </div>

      <div class="post-to" @click="openPostTo()">
        <div class="post-to-fake-input">
          <span v-for="(item, index) in product.post_to.data" :key="index">
            {{ item.tag ? `${item.tag} (${item.creator_count})` : item.name }}
          </span>
        </div>
        <el-button>
          {{ product.post_to.data.length > 0 ? 'Edit' : 'Add' }}
        </el-button>
      </div>
    </div>

    <div class="carded">
      <div class="carded-header">
        <div class="carded-title">Collections</div>
        <el-button v-if="collectionList?.length !== 0" type="text" @click="createNewCollection"
          class="create-new-btn">Create new</el-button>
      </div>

      <div class="collections-content">
        <div v-if="isLoadingCollections" class="loading-state">
          <div class="loading-message">Loading collections...</div>
        </div>
        <div v-else-if="collectionList?.length === 0" class="empty-state">
          <el-button type="text" @click="createNewCollection" class="create-first-btn">
            No collections, Create your first collection
          </el-button>
        </div>
        <div v-else>
          <el-select v-model="selectedCollections" placeholder="Select collections" multiple @change="handleCollectionChange" class="collection-select">
            <el-option v-for="collection in collectionList" :key="collection.id" :label="collection.title" :value="collection.id" />
          </el-select>
        </div>
      </div>
    </div>

    <!-- Creator Permissions -->
    <div class="carded">
      <div class="carded-header">
        <div class="carded-title">Creator permissions</div>
      </div>

      <div class="carded-body permissions">
        <div v-for="perm in permissionList" :key="perm.key" class="perm-row" @click="handlePermissionChange(perm.key)">
          <Switch :checked="perm.checked" />
          <div class="perm-label">{{ perm.label }}</div>
        </div>
      </div>
    </div>

    <!-- Disclosures -->
    <div class="carded">
      <div class="carded-header">
        <div class="carded-title">Disclosures</div>
      </div>
      <div class="carded-body">
        <div class="disclosures">
            <Switch :checked="product.is_sponsored_disclosure_enabled"
            @click="changeDis" />
            <span>When selected, this product will show a label on creators' storefronts to
              shoppers to indicate it is part of a brand promotion.</span>
          </div>
      </div>
      <div class="dis-input-box" v-if="product.is_sponsored_disclosure_enabled">
        <el-input :class="['dis-input',product.sponsored_disclosure_name_err && 'err-input']" v-model="product.sponsored_disclosure_name" @change="sponsoredNameChange()" maxlength="30" show-word-limit/>
        <p class="red-text" v-if="product.sponsored_disclosure_name_err">{{ product.sponsored_disclosure_name_err }}</p>
      </div>
    </div>

    <!-- Schedule -->
    <div class="carded">
      <div class="carded-header">
        <div class="carded-title">Schedule (UTC)</div>
      </div>
      <!-- (product.status_string !== 'active' && product.status_string !== 'disable') || product.is_draft -->
      <!--(product.status_string !== 'active' && product.status_string !== 'disable') || product.is_draft-->
      <ScheduleCard
        :publishEnabled="product.scheduled_start_enabled"
        :publishDate="product.scheduled_start"
        :showPublishSwitch="true"
        :publishedAtText="product.published_at && !product.scheduled_start_enabled ? formatDateTime(product.published_at) : ''"
        :disableEnabled="product.scheduled_end_enabled"
        :disableDate="product.scheduled_end"
        :showDisableSwitch="true"
        @toggle:publish="handlePublishEnabledChange"
        @toggle:disable="handleDisableEnabledChange"
        @update:publishDate="handlePublishDate"
        @update:publishTime="handlePublishTime"
        @update:disableDate="handleDisableDate"
        @update:disableTime="handleDisableTime"
      />
    </div>

    <div class="carded-right">
      <div class="w-100" v-if="product.is_draft">
        <el-button text>
          <Checkbox :label_text="'Mark as draft'" :checked="product.status_string === 'draft'"
            @click="product.status_string = product.status_string === 'draft' ? 'active' : 'draft'" />
        </el-button>
      </div>
      <div class="w-100" @click="emit('delete:product')" v-if="product?.status_string === 'draft' && product.from_table"><el-button type="text">Delete</el-button></div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch, getCurrentInstance } from 'vue';
import { ElButton, ElSelect, ElOption, ElInput } from 'element-plus';
import Checkbox from '@/components/ui/Checkbox.vue';
import Switch from '@/components/ui/Switch.vue';
import ScheduleCard from '@/components/storefront/ScheduleCard.vue';
import { useStorefrontFunction } from '@/components/storefront/StorefrontFunction.js';
import moment from 'moment';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const event = app.appContext.config.globalProperties.EventBus;

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
});

const emit = defineEmits([
  'update:product',
  'delete:product'
]);

// Collections data
const collectionList = ref([]);
const selectedCollections = ref([]);
const isLoadingCollections = ref(true);

// Get storefront functions
const { openRecipientEditDialog, STATUS_CODE } = useStorefrontFunction();

// Computed property to check if any scheduling is enabled
const isScheduled = computed(() => {
  return (props.product.scheduled_start_enabled || props.product.scheduled_end_enabled ) && props.product.status !==STATUS_CODE.Active&& props.product.status !==STATUS_CODE.Disabled;
});

const isDraft = computed(() => {
  return props.product.status_string === 'draft';
})

// const disableMinDate= computed(()=>{
//   if( props.product.scheduled_start_enabled && props.product.scheduled_start && props.product.scheduled_start.val){
//     return new Date(props.product.scheduled_start.val);
//   }else{
//     return new Date();
//   }
// });

// Watch for scheduling changes to update status
watch(isScheduled, (newValue) => {
  if (newValue) {
    // When scheduling is enabled, set status to 'scheduled'
    updateFormData({ status_string: 'scheduled' });
  } else if (props.product.status_string === 'scheduled') {
    // When scheduling is disabled and status was 'scheduled', reset to 'active'
    updateFormData({ status_string: 'active' });
  }
});

// Define permission configuration
const permissionConfig = [
  { key: 'allow_feature', label: 'Feature on creators’ storefront homepages' },
  { key: 'allow_remove', label: 'Allow creators to remove this product from their storefronts' },
];

// Computed property to transform permission values to display format
const permissionList = computed(() => {
  return permissionConfig.map(config => ({
    key: config.key,
    label: config.label,
    checked: props.product.permission?.includes(config.key) || false
  }));
});

// Load collections data
const loadCollections = async () => {
  try {
    isLoadingCollections.value = true;
    let page = 1;
    let allCollections = [];
    let hasMore = true;

    while (hasMore) {
      const response = await ajax.get('/storefront-curation/get-curation-collection-list', {
        params: { page },
      });

      const data = response.data.data.data;
      allCollections = allCollections.concat(data);

      // Check if this is the last page
      const totalPages = response.data.data.total_pages; // adjust if your API returns total_pages
      if (response.data.data.current_page >= response.data.data.last_page || data.length === 0) {
        hasMore = false;
      } else {
        page++;
      }
    }

    collectionList.value = allCollections;
  } catch (error) {
    console.error('Failed to load collections:', error);
    collectionList.value = [];
  } finally {
    isLoadingCollections.value = false;
  }
};

// Handle collection change
const handleCollectionChange = (collectionIds) => {
  const collections = collectionList.value.filter(c => collectionIds.includes(c.id));

  updateFormData({
    curation_collections: collections
  });
};

// Create new collection
const createNewCollection = () => {
  // TODO: Implement create new collection logic
  // console.log('Create new collection clicked');
  event.emit('open-collection-edit-dialog', {
    type: 'is-need-refresh'
  });
};

const sponsoredNameChange=()=>{
  emit('update:product',{...props.product})
}
// Remove a specific collection
// const removeCollection = (collectionId) => {
//   const newCollectionIds = selectedCollections.value.filter(id => id !== collectionId);
//   selectedCollections.value = newCollectionIds;

//   const collections = collectionList.value.filter(c => newCollectionIds.includes(c.id));

//   updateFormData({ 
//     collections: collections
//   });
// };

// Initialize selected collections from product data
const initializeCollection = () => {
  if (props.product.curation_collections && Array.isArray(props.product.curation_collections)) {
    selectedCollections.value = props.product.curation_collections.map(c => c.id);

    // console.log('Initialized from curation_collections', selectedCollections.value);
  } 
  // else if (props.product.collections && Array.isArray(props.product.collections)) {
  //   selectedCollections.value = props.product.collections.map(c => c.id);
  // } 
  else if (props.product.collection_ids && Array.isArray(props.product.collection_ids)) {
    // Backward compatibility for collection_ids array
    selectedCollections.value = [...props.product.collection_ids];

    // Convert to new format if we have names too
    if (props.product.collection_names) {
      const collections = props.product.collection_ids.map((id, index) => ({
        id: id,
        title: props.product.collection_names[index] || '',
        name: props.product.collection_names[index] || ''
      }));

      // Update to new format
      updateFormData({ collections: collections });
    }
  } else if (props.product.collection_id) {
    // Backward compatibility for single collection
    selectedCollections.value = [props.product.collection_id];

    if (props.product.collection_name) {
      const collection = {
        id: props.product.collection_id,
        title: props.product.collection_name,
        name: props.product.collection_name
      };

      // Update to new format
      updateFormData({ collections: [collection] });
    }
  } else {
    selectedCollections.value = [];
  }
};

onMounted(() => {
  if (isLoadingCollections.value) loadCollections();
  initializeCollection();

  event.on('refresh-product-collection-list', () => {
    loadCollections();
  });

});

// Watch for product changes to reinitialize collections
watch(() => props.product, () => {
  initializeCollection();
}, { deep: true });

// General method for updating data
const updateFormData = (updates) => {
  const newData = { ...props.product, ...updates };
  emit('update:product', newData);
};

// General method for updating nested objects
// const updateNestedData = (path, value) => {
//   const newData = { ...props.product };
//   const pathArray = path.split('.');
//   const target = pathArray.slice(0, -1).reduce((obj, key) => obj[key], newData);
//   const lastKey = pathArray[pathArray.length - 1];
//   target[lastKey] = value;
//   emit('update:product', newData);
// };

// Internal method for handling permission changes
const handlePermissionChange = (permKey) => {
  // console.log('Permission change clicked:', permKey);
  const newData = { ...props.product };
  if (!newData.permission) {
    newData.permission = [];
  }

  const currentPermissions = [...newData.permission];
  const index = currentPermissions.indexOf(permKey);

  if (index > -1) {
    // Remove permission
    currentPermissions.splice(index, 1);
  } else {
    // Add permission
    currentPermissions.push(permKey);
  }

  newData.permission = currentPermissions;
  // console.log('Emitting updated product:', newData);
  emit('update:product', newData);
};

// Internal method for handling publish status changes
const handlePublishEnabledChange = () => {
  // if ((props.product.status_string === 'active' || props.product.status_string === 'disable') && !props.product.is_draft) {
  //   return; // Do nothing if status is Active or Disabled
  // }
  const newData = { ...props.product };
  newData.scheduled_start_enabled = !props.product.scheduled_start_enabled;

  // Initialize scheduled_start with correct format when enabling
  if (newData.scheduled_start_enabled && (!newData.scheduled_start || typeof newData.scheduled_start !== 'object')) {
    newData.scheduled_start = {
      date: '',
      time: '12:00',  // Set default time when enabling
      val: ''
    };
  }

  emit('update:product', newData);
};

// Internal method for handling disable status changes
const handleDisableEnabledChange = () => {
  // if ((props.product.status_string === 'active' || props.product.status_string === 'disable' ) && !props.product.is_draft) {
  //   return; // Do nothing if status is Active or Disabled
  // }
  const newData = { ...props.product };
  newData.scheduled_end_enabled = !props.product.scheduled_end_enabled;

  // Initialize scheduled_end with correct format when enabling
  if (newData.scheduled_end_enabled && (!newData.scheduled_end || typeof newData.scheduled_end !== 'object')) {
    newData.scheduled_end = {
      date: '',
      time: '',
      val: ''
    };
  }

  emit('update:product', newData);
};

// Internal method for handling notify creators changes
// const handleNotifyCreatorsChange = () => {
//   updateFormData({ notify_creators: !props.product.notify_creators });
// };

// Helper function to format datetime string
const formatDateTime = (date, time) => {
  // console.log('Formatting date and time:', date, time);
  const formattedDate = moment(date).format('YYYY-MM-DD');
  let finalTime = '12:00:00';
  if (time && time.trim() !== '') {
    // Ensure time is in HH:MM:SS format
    finalTime = moment(time, ['HH:mm', 'HH:mm:ss', 'h:mm A']).format('HH:mm:ss');
  }

  // console.log('Formatted datetime:', `${formattedDate} ${finalTime}`);


  return `${formattedDate} ${finalTime}`;
};

// Internal method for handling publish date changes
const handlePublishDate = (date) => {
  const dateValue = typeof date === 'object' ? date.val : date;
  const newData = { ...props.product };

  if (!newData.scheduled_start) {
    newData.scheduled_start = { date: '', time: '', val: '' };
  }

  newData.scheduled_start.date = dateValue;

  // If no time is set, set default time to 12:00 and sync it to the time field
  // if (newData.scheduled_start.date && (!newData.scheduled_start.time || newData.scheduled_start.time.trim() === '')) {
  //   newData.scheduled_start.time = '12:00';
  // }

  // Update val with formatted datetime
  if (newData.scheduled_start.date) {
    newData.scheduled_start.val = formatDateTime(newData.scheduled_start.date, newData.scheduled_start.time);
  } else {
    newData.scheduled_start.val = '';
  }

  emit('update:product', newData);
};

// Internal method for handling publish time changes
const handlePublishTime = (time) => {
  const timeValue = typeof time === 'object' ? time.val : time;
  const newData = { ...props.product };

  if (!newData.scheduled_start) {
    newData.scheduled_start = { date: '', time: '', val: '' };
  }

  newData.scheduled_start.time = timeValue;

  // Update val with formatted datetime
  if (newData.scheduled_start.date) {
    newData.scheduled_start.val = formatDateTime(newData.scheduled_start.date, newData.scheduled_start.time);
  } else if (newData.scheduled_start.time) {
    // If only time is provided, we can't create a full datetime
    newData.scheduled_start.val = '';
  }

  emit('update:product', newData);
};

// Internal method for handling disable date changes
const handleDisableDate = (date) => {
  const dateValue = typeof date === 'object' ? date.val : date;
  const newData = { ...props.product };

  if (!newData.scheduled_end) {
    newData.scheduled_end = { date: '', time: '', val: '' };
  }

  newData.scheduled_end.date = dateValue;

  // If no time is set, set default time to 12:00 and sync it to the time field
  // if (newData.scheduled_end.date && (!newData.scheduled_end.time || newData.scheduled_end.time.trim() === '')) {
  //   newData.scheduled_end.time = '12:00';
  // }

  // Update val with formatted datetime
  if (newData.scheduled_end.date) {
    newData.scheduled_end.val = formatDateTime(newData.scheduled_end.date, newData.scheduled_end.time);
  } else {
    newData.scheduled_end.val = '';
  }

  emit('update:product', newData);
};

// Internal method for handling disable time changes
const handleDisableTime = (time) => {
  const timeValue = typeof time === 'object' ? time.val : time;
  const newData = { ...props.product };

  if (!newData.scheduled_end) {
    newData.scheduled_end = { date: '', time: '', val: '' };
  }

  newData.scheduled_end.time = timeValue;

  // Update val with formatted datetime
  if (newData.scheduled_end.date) {
    newData.scheduled_end.val = formatDateTime(newData.scheduled_end.date, newData.scheduled_end.time);
  } else if (newData.scheduled_end.time) {
    // If only time is provided, we can't create a full datetime
    newData.scheduled_end.val = '';
  }

  emit('update:product', newData);
};

// Internal method for opening post to dialog
const openPostTo = async () => {
  props.product.click_all = props.product.post_to.data.length === 0;
  props.product.recipient_groups = props.product.post_to.data.filter(item => !item.motom_user_id);
  props.product.recipient_creators = props.product.post_to.data.filter(item => item.motom_user_id);
  // console.log(props.product)

  const result = await openRecipientEditDialog(props.product);
  updateFormData(result);
};

const changeDis=()=>{
  const newData = { ...props.product };
  newData.is_sponsored_disclosure_enabled = !newData.is_sponsored_disclosure_enabled;
  emit('update:product', newData)
}
</script>

<style lang="scss">
.product-right-col {
  width: 100%;

  .red-text{
    color: var(--red);
  }

  &>div {
    margin-bottom: 16px;
  }

  .status-select {
    display: flex;
    align-items: center;

    label {
      margin-right: 8px;
      flex: 1;
      font: var(--medium-16);
      color: var(--Text-primary);
    }

    .el-select {
      max-width: 100px;
    }

    .status-display {
      max-width: 100px;
      padding: 5px 10px;
      background-color: var(--el-input-bg-color, var(--el-fill-color-blank));
      border: 1px solid var(--el-input-border-color, var(--el-border-color));
      border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
      color: var(--Text-primary);
      text-align: center;
    }
  }

  .carded {
    border-radius: 12px;
    background-color: var(--Bg-01);
    padding: 16px;

    .carded-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .carded-title {
        font: var(--medium-16);
        color: var(--Text-primary);
        flex: 1;

        .required{
          color:var(--red);
          &::after {
            display: none;
          }
        }
      }

      .create-new-btn {
        color: var(--Blue-30);
        padding: 0;
        height: auto;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .selected-info {
      display: flex;

      .select-row {
        flex: 1;
      }
    }

    .permissions {

      .perm-row {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;
        margin: 8px 0;
        color: var(--Text-primary);

        .ui-switch {
          position: relative;
          top: 2px;
        }
      }
    }
  }

  .collections-content {
    margin-top: 12px;

    .collection-select {
      width: 100%;
      margin-bottom: 8px;
    }

    .selected-collection {
      padding: 8px 12px;
      background-color: var(--Bg-02);
      border-radius: 6px;
      font-size: 14px;
      color: var(--Text-primary);
      border: 1px solid var(--Border-light);
    }

    .empty-state,
    .loading-state {
      text-align: center;
      padding: 16px;

      .empty-message,
      .loading-message {
        color: var(--Text-secondary);
        font-size: 14px;
        margin-bottom: 12px;
      }

      .create-first-btn {
        color: var(--Blue-30);
        padding: 0;
        height: auto;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .loading-state {
      .loading-message {
        margin-bottom: 0;
      }
    }
  }

  .el-select__selection.is-near {
    margin-left: -5px;
  }

  .post-to {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;

    .post-to-fake-input {
      display: inline-flex;
      flex-grow: 1;
      align-items: center;
      justify-content: flex-start;
      background-color: var(--el-input-bg-color, var(--el-fill-color-blank));
      background-image: none;
      border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
      transition: var(--el-transition-box-shadow);
      transform: translate3d(0, 0, 0);
      box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;
      height: (--el-input-inner-height);
      flex-wrap: wrap;
      gap: 8px;
      min-height: 32px;

      span {
        font: var(--medium-12);
        color: var(--Text-primary);
        padding: 4px 8px;
        background-color: var(--Black-05);
        border-radius: 4px;
        border: 1px solid var(--Border-light);

        &:first-child {
          margin-left: 4px;
        }
      }
    }
  }

  .carded-right {
    margin-top: -8px;

    .w-100 {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      gap: 8px;

      .el-button--text {
        color: var(--red);
      }
    }
  }

  .carded-body{
    .disclosures{
      display: flex;
      gap:8px;
      margin-top: 8px;

      .ui-switch{
        margin-top: 2px;
      }
    }
  }
}
</style>